import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import {
	catchError,
	finalize,
	map,
	shareReplay,
	switchMap,
} from 'rxjs/operators';

// --- Facades ---
import { AuthFacade } from '@facades/auth.facade';

// --- Utils ---
import {
	isRefreshTokenExpired,
	resolveAccessToken,
} from '@utils/auth-session.util';
import { authNavigation } from '@utils/redirect-to-auth.util';

// --- Interfaces ---
import type { ITokens, IUser } from '@interfaces/user';

const INITIAL_STATE = {
	token: null,
	refreshToken: null,
	refreshTokenExpiresAt: null,
};

/** Auth endpoints that must not trigger a refresh loop on 401. */
const AUTH_CREDENTIAL_URL = /auth\/(refresh|login|guest-token)(?:\?|$)/;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
	private tokens: ITokens = INITIAL_STATE;

	private readonly tokens$ = this.authFacade.authState$.pipe(
		map(({ user }) => {
			return {
				token: user?.token,
				refreshToken: user?.refreshToken,
				refreshTokenExpiresAt: user?.refreshTokenExpiresAt,
			};
		})
	);

	/** Shared in-flight refresh so concurrent 401s wait on one call. */
	private refreshInFlight$: Observable<string> | null = null;

	constructor(private authFacade: AuthFacade) {
		this.tokens$.subscribe(tokens => (this.tokens = tokens));
	}

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		let token = this.tokens.token;

		if (!token) {
			token = JSON.parse(localStorage.getItem('guestToken') as string);
		}

		if (!token) return this.interceptWithTimeout(request, next);

		request = this.addTokenToHeader(request, token);

		return next
			.handle(request)
			.pipe(catchError(error => this.handle401Error(error, request, next)));
	}

	interceptWithTimeout(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		return new Observable(observer => {
			const token =
				this.tokens.token ||
				JSON.parse(localStorage.getItem('guestToken') as string);

			setTimeout(() => {
				if (token) request = this.addTokenToHeader(request, token);

				next.handle(request).subscribe({
					next: event => observer.next(event),
					error: error => {
						this.handle401Error(error, request, next).subscribe({
							next: event => observer.next(event),
							error: err => observer.error(err),
							complete: () => observer.complete(),
						});
					},
					complete: () => observer.complete(),
				});
			});
		});
	}

	addTokenToHeader(request: HttpRequest<any>, token: ITokens['token']) {
		return request.clone({
			setHeaders: {
				Authorization: `Bearer ${token}`,
			},
		});
	}

	handleUnauthorizedError(request: HttpRequest<any>, next: HttpHandler) {
		if (!this.refreshInFlight$) {
			this.refreshInFlight$ = this.authFacade
				.refreshToken({
					accessToken: this.tokens.token as string,
					refreshToken: this.tokens.refreshToken as string,
				})
				.pipe(
					map((user: IUser) => {
						const token = resolveAccessToken(user);
						if (!token) {
							throw new Error('Refresh response missing access token');
						}
						return token;
					}),
					catchError(error => {
						this.authFacade.logout();
						return throwError(() => error);
					}),
					finalize(() => {
						this.refreshInFlight$ = null;
					}),
					shareReplay(1)
				);
		}

		return this.refreshInFlight$.pipe(
			switchMap(token => next.handle(this.addTokenToHeader(request, token))),
			catchError(error => throwError(() => error))
		);
	}

	private handle401Error(
		error: unknown,
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
			return throwError(() => error);
		}

		if (this.isAuthCredentialRequest(request)) {
			this.forceUnauthenticated();
			return throwError(() => error);
		}

		if (this.tokens.token && this.tokens.refreshToken) {
			if (isRefreshTokenExpired(this.tokens)) {
				this.authFacade.logout();
				return throwError(() => error);
			}

			return this.handleUnauthorizedError(request, next);
		}

		if (this.tokens.token) {
			this.authFacade.logout();
			return throwError(() => error);
		}

		localStorage.removeItem('guestToken');
		authNavigation.redirectToAuthPage();

		return throwError(() => error);
	}

	private isAuthCredentialRequest(request: HttpRequest<any>): boolean {
		return AUTH_CREDENTIAL_URL.test(request.url);
	}

	private forceUnauthenticated() {
		if (this.tokens.token) {
			this.authFacade.logout();
			return;
		}

		localStorage.removeItem('guestToken');
		authNavigation.redirectToAuthPage();
	}
}
